import {
  Button,
  createStyles,
  Grid,
  Typography,
  WithStyles,
  withStyles
} from "@material-ui/core";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import colors from "../../../../../theme/colors";
import { Select } from "../../../../../components/inputs/BoldInput";
import {
  fetchFreezeExemptContractTypes,
  getFreezeExemptContractTypes,
  getFreezeExemptContractTypesSaving,
  getIsFrozen,
  saveFreezeExemptContractTypes
} from "../../../../../modules/factoringClient";
import { ALL_CONTRACT_TYPES } from "../../../../../helpers/contractTypes";

const styles = () =>
  createStyles({
    container: {
      marginTop: 20,
      marginBottom: 8
    },
    sectionHeader: {
      fontWeight: 600,
      marginBottom: 8
    },
    selectWrapper: {
      maxWidth: 380
    },
    // The shared BoldInput Select renders MenuItems and ListSubheaders
    // as siblings inside the menu, so option rows have no natural indent
    // under their headers. Bump the left padding on items (kept local to
    // this menu via the MenuProps.classes.paper hook) so grouping reads
    // visually.
    //
    // The selector targets the JSS-suffixed root class MUI v3 generates
    // for MenuItem (e.g. MuiMenuItem-root-575) — the bare global
    // `.MuiMenuItem-root` selector doesn't catch it in this codebase's
    // MUI setup. Two attribute selectors cover both positions in the
    // className list: `^=` when the suffixed class is first, and `*=`
    // with a leading space when it appears later. ListSubheader uses
    // its own root class, so headers keep their default padding.
    menuPaper: {
      "& [class^='MuiMenuItem-root-'], [class*=' MuiMenuItem-root-']": {
        paddingLeft: 36
      }
    },
    help: {
      color: colors.grayText,
      fontStyle: "italic",
      marginTop: 4,
      marginBottom: 8
    },
    actions: {
      marginTop: 8,
      display: "flex",
      alignItems: "center",
      "& > *:not(:last-child)": {
        marginRight: 8
      }
    }
  });

interface Props extends WithStyles<typeof styles> {
  factoringId: string;
}

const arraysEqualAsSets = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  const set = new Set(a);
  return b.every(v => set.has(v));
};

const FreezeExemptContractTypesForm = ({
  classes,
  factoringId
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const stored = useSelector(getFreezeExemptContractTypes) as string[];
  const saving = useSelector(getFreezeExemptContractTypesSaving);
  const isFrozen = useSelector(getIsFrozen);

  const [selected, setSelected] = useState<string[]>(stored);

  useEffect(() => {
    if (factoringId) {
      dispatch(fetchFreezeExemptContractTypes(factoringId));
    }
  }, [dispatch, factoringId]);

  useEffect(() => {
    setSelected(stored);
  }, [stored]);

  const isDirty = useMemo(
    () => !arraysEqualAsSets(selected, stored),
    [selected, stored]
  );

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
    setSelected(event.target.value as string[]);
  };

  const handleSave = (): void => {
    dispatch(saveFreezeExemptContractTypes(factoringId, selected));
  };

  return (
    <Grid container direction="column" className={classes.container}>
      <Typography variant="subtitle1" className={classes.sectionHeader}>
        Freeze-Exempt Contract Types
      </Typography>
      <div className={classes.selectWrapper}>
        <Select
          fullWidth
          name="freezeExemptContractTypes"
          value={selected}
          onChange={handleChange}
          selections={ALL_CONTRACT_TYPES}
          selectProps={{
            multiple: true,
            MenuProps: { classes: { paper: classes.menuPaper } }
          }}
          withHeader
          disabled={!isFrozen}
        />
      </div>
      <Typography variant="caption" className={classes.help}>
        Selected contract types remain editable when receivables are frozen.
      </Typography>
      <div className={classes.actions}>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={handleSave}
          disabled={!isFrozen || !isDirty || saving}
        >
          {saving ? "Saving…" : "Save Exempt Types"}
        </Button>
      </div>
    </Grid>
  );
};

export default withStyles(styles)(FreezeExemptContractTypesForm);
